// A note's own parts, read from its exact text: its frontmatter as properties, the place of each checkbox (so a
// tick changes that one character and nothing else), and the part of it under one heading (for an embed of it).

/** The frontmatter between the --- lines at the very top, and where the writing after it starts. */
export function splitFrontmatter(text: string): { fm: string | null; body: string; at: number } {
  const m = /^(﻿?)---[ \t]*\r?\n/.exec(text);
  if (!m) return { fm: null, body: text, at: 0 };
  const end = /(^|\r?\n)(---|\.\.\.)[ \t]*(\r?\n|$)/g;
  end.lastIndex = m[0].length - 1;
  let hit: RegExpExecArray | null;
  while ((hit = end.exec(text))) {
    if (hit.index < m[0].length - 1) continue;
    const close = hit.index + hit[1].length;
    const after = hit.index + hit[0].length;
    return { fm: text.slice(m[0].length, close).replace(/\r?\n$/, ""), body: text.slice(after), at: after };
  }
  return { fm: null, body: text, at: 0 };
}

export type PropValue = string | number | boolean | null | PropValue[];
export interface Property {
  key: string;
  value: PropValue;
}

const scalar = (raw: string): PropValue => {
  const s = raw.trim();
  if (!s || s === "~" || s === "null") return null;
  if (/^(true|false)$/i.test(s)) return s.toLowerCase() === "true";
  if (/^-?\d+(\.\d+)?$/.test(s) && s.length < 16) return Number(s);
  const q = /^"((?:[^"\\]|\\.)*)"$|^'((?:[^']|'')*)'$/.exec(s);
  if (q) return q[1] !== undefined ? q[1].replace(/\\(.)/g, "$1") : q[2].replace(/''/g, "'");
  return s;
};

/** A flow list, [a, "b, c", d], split on the commas outside quotes. */
function flowList(s: string): PropValue[] {
  const out: PropValue[] = [];
  let cur = "";
  let quote: string | null = null;
  for (const ch of s) {
    if (quote) {
      cur += ch;
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      cur += ch;
    } else if (ch === ",") {
      if (cur.trim()) out.push(scalar(cur));
      cur = "";
    } else cur += ch;
  }
  if (cur.trim()) out.push(scalar(cur));
  return out;
}

/**
 * Frontmatter as Obsidian's properties: key: value, key: [a, b] and key: with - items under it. Anything more
 * than that (nested maps, multi-line text) is null, and the board shows the frontmatter as written instead.
 */
export function readProperties(fm: string): Property[] | null {
  const out: Property[] = [];
  const lines = fm.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim() || /^\s*#/.test(line)) continue;
    const m = /^([^\s:#][^:]*?)\s*:(?:\s+(.*)|\s*)$/.exec(line);
    if (!m) return null;
    const key = m[1].trim();
    // a # after a space starts a comment, as in YAML, but not inside quotes
    const raw = (m[2] ?? "").trim();
    const quoted = /^("(?:[^"\\]|\\.)*"|'(?:[^']|'')*')/.exec(raw);
    const rest = quoted ? quoted[1] : raw.replace(/\s+#.*$/, "").trim();
    if (!rest) {
      // a list under it, or nothing
      const items: PropValue[] = [];
      while (i + 1 < lines.length && /^\s*-\s*/.test(lines[i + 1])) {
        i++;
        items.push(scalar(lines[i].replace(/^\s*-\s*/, "")));
      }
      if (i + 1 < lines.length && /^\s+\S/.test(lines[i + 1])) return null;
      out.push({ key, value: items.length ? items : null });
      continue;
    }
    if (/^[|>]/.test(rest) || /^\{/.test(rest)) return null;
    if (/^\[.*\]$/.test(rest)) out.push({ key, value: flowList(rest.slice(1, -1)) });
    else out.push({ key, value: scalar(rest) });
  }
  return out;
}

/** Tags from the frontmatter (tags: a, tags: [a, b], or a list), without #. */
export function frontmatterTags(props: Property[] | null): string[] {
  const t = props?.find((p) => /^tags?$/i.test(p.key))?.value;
  const list = Array.isArray(t) ? t : typeof t === "string" ? t.split(/[,\s]+/) : [];
  return list.map((x) => String(x ?? "").replace(/^#/, "").trim()).filter(Boolean);
}

const FENCE = /^\s{0,3}(`{3,}|~{3,})/;
const TASK = /^((?:\s*>)*\s*(?:[-*+]|\d{1,9}[.)])\s+)\[([ xX])\](?=\s+\S)/;

/** Each checkbox in the note, in order, by the place of the character between its brackets in the whole text. */
export function taskBoxes(text: string): { at: number; done: boolean; line: number }[] {
  const { at: start } = splitFrontmatter(text);
  const out: { at: number; done: boolean; line: number }[] = [];
  let pos = start;
  let fence: string | null = null;
  let lineNo = start ? text.slice(0, start).split(/\r\n|\n|\r/).length - 1 : 0;
  const re = /([^\r\n]*)(\r\n|\n|\r|$)/g;
  re.lastIndex = start;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) && m[0].length) {
    const line = m[1];
    lineNo++;
    const f = FENCE.exec(line);
    if (fence) {
      if (f && f[1][0] === fence[0] && f[1].length >= fence.length && !line.trim().slice(f[1].length).trim()) fence = null;
    } else if (f) fence = f[1];
    else {
      const t = TASK.exec(line);
      if (t) out.push({ at: pos + t[1].length + 1, done: t[2] !== " ", line: lineNo });
    }
    pos += m[0].length;
  }
  return out;
}

/** The part of a note under a heading, up to the next heading as big or bigger; null when it has none such. */
export function sectionOf(body: string, heading: string): string | null {
  const want = heading.trim().toLowerCase();
  const lines = body.split(/\r?\n/);
  let from = -1;
  let level = 0;
  let fence: string | null = null;
  for (let i = 0; i < lines.length; i++) {
    const f = FENCE.exec(lines[i]);
    if (fence) {
      if (f && f[1][0] === fence[0]) fence = null;
      continue;
    }
    if (f) {
      fence = f[1];
      continue;
    }
    const h = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/.exec(lines[i]);
    if (!h) continue;
    if (from >= 0 && h[1].length <= level) return lines.slice(from, i).join("\n");
    if (from < 0 && h[2].trim().toLowerCase() === want) {
      from = i;
      level = h[1].length;
    }
  }
  return from >= 0 ? lines.slice(from).join("\n") : null;
}

/** A heading's words as an anchor compares them: case and spaces aside. */
export const headingKey = (s: string) => s.replace(/[*_`[\]]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
